<template>
  <div
    class="-m-2 flex flex-row items-center gap-4 rounded p-2 hover:bg-gray-200"
    data-avatar-shape="square"
  >
    <UserAvatar :user-id="user.id" size="large" hide-status flat />
    <div class="flex min-w-0 flex-1 flex-col">
      <h3 class="text-base leading-tight font-bold">
        {{ user.full_name }}
      </h3>
      <div
        v-if="hasOccupations"
        data-tint="warning"
        class="flex flex-wrap gap-0.5 text-xs"
      >
        <div
          v-for="event in occupations"
          :key="event.id"
          class="bg-primary text-contrast-primary flex flex-col rounded px-1"
        >
          {{ event.title ?? $t(`enums.event.type.${event.type}`) }} -
          {{ $h.rangeDateAsString(event.date_from, event.date_to) }}
        </div>
      </div>
      <div v-if="hasAvailabilityItems" class="flex w-full flex-col">
        <table class="border-separate border-spacing-y-1.5">
          <tbody>
            <AvailabilityItem
              v-for="(availabilityItem, key) in localAvailabilityItems"
              :key="key"
              :item="availabilityItem"
            />
          </tbody>
        </table>
      </div>
    </div>
    <div v-if="editionMode" class="flex shrink-0 items-center gap-1">
      <BaseButton
        v-for="assignableStatus in assignableStatuses"
        :key="assignableStatus.id"
        :data-tint="assignableStatus.bg_color"
        :title="assignableStatus.name"
        icon="plus"
        size="extra-small"
        inverse-icon-order
        @click="addUser(assignableStatus)"
      />
    </div>
  </div>

  <ConfirmModal ref="availabilityAlertModal" />
</template>

<script>
import { defineComponent } from 'vue'
import BaseDropDownMenu from '@c/addf-package/components/dropdown/BaseDropDownMenu.vue'
import UserAvatar from '@c/Avatar/UserAvatar.vue'
import { mapActions, mapGetters } from 'vuex'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import AvailabilityItem from '@c/Dashboard/Content/Planning/components/AvailabilityItem.vue'
import ConfirmModal from '@c/addf-package/components/modal/ConfirmModal.vue'

export default defineComponent({
  name: 'SelectableEventUser',
  components: {
    ConfirmModal,
    AvailabilityItem,
    BaseButton,
    UserAvatar,
    BaseDropDownMenu
  },
  props: {
    user: {
      type: Object,
      required: true
    },
    event: {
      type: Object,
      required: true
    },
    editionMode: {
      type: Boolean,
      required: false,
      default: false
    },
    occupations: {
      type: Array,
      required: true
    },
    allUsersAvailabilityItems: {
      type: Array,
      required: false,
      default: () => []
    }
  },
  emits: ['add-user'],
  computed: {
    ...mapGetters({
      assignementStatuses: 'metadata/getAssignmentStatusMetadata'
    }),
    hasOccupations() {
      return Array.isArray(this.occupations) && this.occupations.length > 0
    },
    hasAvailabilityItems() {
      return this.localAvailabilityItems.length > 0
    },
    localAvailabilityItems() {
      return this.allUsersAvailabilityItems.filter(
        (availabilityItem) => availabilityItem.model_id === this.user.id
      )
    },
    assignableStatuses() {
      return this.assignementStatuses.filter((assignedStatuse) =>
        ['confirmed', 'pending'].includes(assignedStatuse.id)
      )
    }
  },
  methods: {
    async addUser(assignableStatus) {
      let response = true

      let confirm =
        !this.hasAvailabilityItems ||
        (await this.$refs.availabilityAlertModal.show({
          title: this.$t('alert.absence_alert'),
          message: this.$t(`alert.absence_alert_message_user`, {
            name: this.$trans(this.user.full_name),
            event: this.event.title
          }),
          okButton: this.$t('button.add_anyway')
        }))

      if (confirm !== response) {
        response = confirm
      }

      if (response) {
        this.$emit('add-user', {
          user_id: this.user.id,
          assignement_status: assignableStatus.id
        })
      }
    }
  }
})
</script>

<style scoped></style>
